import Image from "next/image";
import Popover from "../popover/popover";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../redux/hooks";
import { API } from "../../../../../services";
import { conditions, numbersToLetters, responseValidator } from "../../../../../helpers";
import { ConditionName, ContentTypes, IPlayListCardProps } from "../../../../../interfaces";
import { handleClickPlayerCardTag } from "../../../../../redux/actions/tags.actions"
import { setPlayListCardPopover } from "../../../../../redux/slices/popoversSlice";
import CloseIcon from "../../icon-components/closeIcon";
import { shallowEqual } from 'react-redux';
import dynamic from "next/dynamic";
import { getPlayListCards } from 'redux-share/actions'

const TruncateMarkup = dynamic(() => import("react-truncate-markup"));
const PlayerCard = dynamic(() => import("../playerCard/playerCard"));
const PlayListIcon = dynamic(() => import("../../icon-components/playlistIcon"));
const VideoListHeaderIcon = dynamic(() => import("../../icon-components/videoListHeader"));

const PlayListCard = ({ playListCardInfo }: IPlayListCardProps) => {
    const [playList, setPlayList] = useState<any[]>([]);
    const [showAllDescription, setShowAllDescription] = useState<boolean>(false);

    const dispatch = useAppDispatch();
    const { params } = useAppSelector(({ videoData: { params, isComplexDesktop, isComplexPortrait } }) => ({ params, isComplexDesktop, isComplexPortrait }), shallowEqual);
    const { playListCardPopover } = useAppSelector(({ popovers: { playListCardPopover } }) => ({ playListCardPopover }), shallowEqual);
    useEffect(() => {
        if (playListCardPopover) {
            (async () => {
                const { data, status } = await getPlayListCards(API().playListVideos(playListCardInfo.id));
                if (responseValidator(status)) {
                    const sortedVideos: any[] = [];
                    data.results.map((result: any) => {
                        if (sortedVideos[result.video.season - 1]) sortedVideos[result.video.season - 1].push(result.video);
                        else sortedVideos.push([result.video]);
                    });
                    setPlayList(sortedVideos);
                }
            })()
        }
    }, [playListCardPopover])

    const banner = (
        <div className="pl-w-full pl-aspect-video pl-relative">
            {playListCardInfo.banner ? <Image alt="" src={playListCardInfo.banner} layout="fill" objectFit="contain" className="pl-rounded-[16px]" /> : (
                <div className="pl-flex pl-justify-center pl-items-center pl-w-full pl-h-full pl-rounded-[16px]" style={{ background: conditions(ConditionName.isDarkMode) ? "radial-gradient(145.00% 58.03% at 50.00% 50.00%, #263238 0%, rgba(41, 45, 50, 0.10) 100%)" : "radial-gradient(102.67% 46.56% at 50.00% 45.38%, rgba(41, 170, 243, 0.80) 0%, rgba(41, 145, 255, 0.20) 100%)" }}>
                    <PlayListIcon className="pl-w-[45px] pl-h-[30px] pl-fill-main dark:pl-fill-white" />
                </div>
            )}
        </div>
    )

    const title = (
        <>
            {!!playListCardInfo.title && (
                <p className="pl-flex pl-gap-2 pl-text-4 pl-font-bold">
                    <span>{playListCardInfo.title}</span>
                    {playListCardInfo.content_type === ContentTypes.PlayList && <span className="pl-text-secondary-500 dark:pl-text-z-gray-400">(سریال)</span>}
                </p>
            )}
        </>
    )

    const tags = (
        <>
            {!!playListCardInfo.tag && (
                <div className={`pl-flex pl-gap-1 pl-flex-wrap`}>
                    {playListCardInfo.tag.split(",").map((tag: string, index: number) =>
                        <p
                            key={index}
                            className={`pl-cursor-pointer pl-bg-main-light dark:pl-bg-main-dark pl-text-z-secondary-50 dark:pl-text-dark-5 pl-px-2 pl-py-[3px] pl-rounded-[4px] pl-font-normal pl-min-w-max pl-text-[10px]`}
                            onClick={(event: any) => handleClickPlayerCardTag(event, tag)}
                        >
                            {tag}
                        </p>
                    )}
                </div>
            )}
        </>
    )

    const imdb_rate = (
        <>
            {!!playListCardInfo.imdb_rate && (
                <div className={`pl-flex pl-items-center pl-mr-auto pl-bg-warning pl-pl-[11px] pl-rounded-[24px] pl-text-secondary-800`}>
                    <div className="pl-bg-orange-100/[0.4] pl-pr-4 pl-rounded-r-[24px] pl-pt-[2px] pl-pl-2 pl-text-[14px] pl-font-extrabold">IMDB</div>
                    <div className="pl-mr-[6px]">
                        <span className="pl-text-[15px] pl-font-bold">{playListCardInfo.imdb_rate}</span>
                        <span>/</span>
                        <span>10</span>
                    </div>
                </div>
            )}
        </>
    )
    const release_year = <>{!!playListCardInfo.release_year && <p className="pl-text-z-secondary-600 dark:pl-text-secondary pl-text-[14px] pl-font-medium">تولید {playListCardInfo.release_year}</p>}</>
    const videoCount = <>{playListCardInfo.count && <p>{playListCardInfo.count} ویدیو</p>}</>
    const closeIcon = <CloseIcon className="pl-stroke-z-gray-700 dark:pl-stroke-dark-5 pl-cursor-pointer" onClick={() => dispatch(setPlayListCardPopover(false))} />
    const description = <p className={`pl-pb-6 pl-text-[12px] pl-font-normal pl-text-justify`}>{playListCardInfo.description}</p>

    return (
        <>
            <Popover
                status={playListCardPopover}
                onClose={() => dispatch(setPlayListCardPopover(false))}
                type="modal"
                classNames="pl-w-screen pl-h-[100dvh] !pl-bg-white dark:!pl-bg-dark-1 !pl-rounded-none"
                isFullScreen={true}
            >
                <>
                    {conditions(ConditionName.isComplexPortrait) ? (
                        <>
                            <div className="pl-flex pl-flex-col pl-gap-2 pl-px-4 pl-pb-3" style={{ background: conditions(ConditionName.isDarkMode) ? "linear-gradient(224deg, #BB86FC 0%, rgba(100, 64, 192, 0.50) 100%)" : "linear-gradient(224deg, rgba(104, 51, 221, 0.40) 0%, rgba(104, 51, 221, 0.00) 100%)" }}>
                                <div className="pl-flex pl-justify-end pl-pt-2">{closeIcon}</div>
                                {banner}
                                {title}
                                <div className="pl-flex pl-gap-2 pl-justify-between pl-items-center">
                                    {tags}
                                    {imdb_rate}
                                </div>
                                {playListCardInfo.content_type === ContentTypes.PlayList ? <>{release_year}</> : <>{videoCount}</>}
                                {showAllDescription ? (
                                    <div className="pl-max-w-screen pl-text-[14px] pl-font-normal dark:pl-text-dark-5 pl-whitespace-pre-wrap pl-flex-1 no-scrollbar pl-text-justify">
                                        {playListCardInfo.description}
                                        {" "}
                                        <span onClick={() => setShowAllDescription(false)} className="pl-text-primary-600">{"کمتر..."}</span>
                                    </div>
                                ) :
                                    (
                                        <TruncateMarkup
                                            ellipsis={() => <span onClick={() => setShowAllDescription(true)} className="pl-text-primary-600">{" بیشتر..."}</span>}
                                            lines={2}
                                        >
                                            <div className="pl-text-[14px] pl-font-normal dark:pl-text-dark-5 pl-flex-1 no-scrollbar pl-text-justify">{playListCardInfo.description}</div>
                                        </TruncateMarkup>
                                    )}
                            </div>
                            <div className={`pl-flex pl-flex-col pl-gap-2 pl-pr-4 pl-pt-4`}>
                                {playList.length > 0 && playList.map((item: any, index: number) => (
                                    <>
                                        <div className="pl-flex pl-items-center pl-gap-3">
                                            <VideoListHeaderIcon className="pl-fill-secondary-800 dark:pl-fill-white" />
                                            <p>فصل {index > 19 ? index + 1 : numbersToLetters[index]}</p>
                                        </div>
                                        {item.map((value: any) => (
                                            <div key={value.id} className="pl-relative pl-overflow-hidden pl-h-[88px]">
                                                <PlayerCard
                                                    src={value.banner}
                                                    type={value.type}
                                                    width={156}
                                                    height={88}
                                                    onClick={() => { }}
                                                    id={value.id}
                                                    duration={value.duration}
                                                    title={value.title}
                                                    titleClassName="!pl-text-[14px] !pl-font-medium pl-text-black dark:pl-text-white"
                                                    tags={value.tag}
                                                    viewers={value.viewers}
                                                    date={value.start_time}
                                                    dateClassName="!pl-text-[9px]"
                                                    star={value.star}
                                                    isComplexDesktop={conditions(ConditionName.isComplexDesktop)}
                                                />
                                            </div>
                                        ))}
                                    </>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="pl-flex pl-flex-col pl-h-screen pl-overflow-hidden">
                            <div className="pl-self-end pl-pl-10 pl-pt-5">
                                {closeIcon}
                            </div>
                            <div className="pl-flex pl-gap-[44px] pl-flex-1 pl-m-10">
                                <aside className="pl-flex pl-flex-col pl-gap-3 pl-w-[360px] pl-overflow-auto pl-h-screen pl-max-h-[90vh] pl-pt-4 pl-px-[22px] no-scrollbar pl-mb-8 pl-rounded-[20px]" style={{ background: conditions(ConditionName.isDarkMode) ? "linear-gradient(224deg, #BB86FC 0%, rgba(100, 64, 192, 0.50) 100%)" : "linear-gradient(224deg, rgba(104, 51, 221, 0.40) 0%, rgba(104, 51, 221, 0.00) 100%)" }}>
                                    <div className="pl-flex">{banner}</div>
                                    {title}
                                    {tags}
                                    {playListCardInfo.content_type === ContentTypes.PlayList ? (
                                        <>
                                            {(!!playListCardInfo.release_year || !!playListCardInfo.imdb_rate) && (
                                                <div className="pl-flex pl-justify-between pl-items-center">
                                                    {release_year}
                                                    {imdb_rate}
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <>{videoCount}</>
                                    )}
                                    {description}
                                </aside>
                                <div className="pl-w-full !pl-overflow-auto !pl-h-screen pl-max-h-[90vh]">
                                    {playList.length > 0 && playList.map((item: any, index: number) => (
                                        <>
                                            {playList.length > 1 && (
                                                <div className="pl-flex pl-items-center pl-gap-3 pl-mb-6">
                                                    <VideoListHeaderIcon className="pl-fill-secondary-800 dark:pl-fill-white" />
                                                    <p className="pl-flex pl-gap-1">
                                                        <span>فصل {index > 19 ? index + 1 : numbersToLetters[index]}</span>
                                                        <span className="pl-text-z-secondary-500">({item.length} قسمت)</span>
                                                    </p>
                                                </div>
                                            )}
                                            <div className="pl-flex pl-flex-col pl-gap-4 pl-mb-4">
                                                {item.map((value: any, index: number) => (
                                                    <div key={value.id} className={`${index % 2 !== 0 && "pl-bg-[#F6F8FF] dark:pl-bg-dark-3"} pl-flex pl-items-center pl-gap-[6px] pl-pr-2 pl-py-4 pl-rounded-[10px] pl-border pl-border-transparent hover:pl-border hover:pl-border-primary-600 pl-h-[120px]`}>
                                                        <p className={`pl-flex pl-justify-center pl-items-center pl-border pl-border-secondary-700 dark:pl-border-white pl-text-black dark:pl-text-white pl-rounded-full pl-w-[25px] pl-h-[25px] pl-text-[14px] pl-font-medium ${value.id === +params.id! && "pl-bg-secondary-700 pl-text-white dark:pl-bg-white dark:pl-text-secondary-700"}`}>{value.episode}</p>
                                                        <PlayerCard
                                                            src={value.banner}
                                                            type={value.type}
                                                            width={156}
                                                            height={88}
                                                            onClick={() => { }}
                                                            id={value.id}
                                                            duration={value.duration}
                                                            title={value.title}
                                                            titleClassName="!pl-text-[14px] !pl-font-medium pl-text-black dark:pl-text-white"
                                                            tags={value.tag}
                                                            viewers={value.viewers}
                                                            date={value.start_time}
                                                            dateClassName="!pl-text-[9px]"
                                                            star={value.star}
                                                            isComplexDesktop={conditions(ConditionName.isComplexDesktop)}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                </>
            </Popover>
        </>
    )
}

export default PlayListCard;